import { addMonths, differenceInMonths, format, parseISO } from "date-fns";
import { FileText } from "lucide-react";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ASSET_CLASS_ORDER } from "@/types/engagement";

import { useRequiredFieldHighlight } from "../hooks/useRequiredFieldHighlight";
import type { ContractFormValues } from "../schemas/contractFormSchema";

import { FormMessageT } from "./FormMessageT";
import { FormSectionCard } from "./FormSectionCard";
import { InfoTooltip } from "./InfoTooltip";

const MIN_TERM_MONTHS = 1;
const MAX_TERM_MONTHS = 1200;

/** Spec §11.15.6 — universal input chrome (visible borders in both themes). */
const INPUT_CHROME = "bg-background border border-input shadow-sm";

/** Spec §11.15.2 — pixel-perfect label container. */
const LABEL_FLEX =
  "flex items-center gap-1.5 text-sm font-medium text-foreground/90";

/**
 * Returns the canonical ISO end date implied by `(commencementDate,
 * leaseTermMonths)` — used as the forward-sync display for the
 * ephemeral End Date input.
 */
function computeEndIso(
  commencementDate: string | undefined,
  leaseTermMonths: number | undefined,
): string | undefined {
  if (!commencementDate || !leaseTermMonths) return undefined;
  if (
    leaseTermMonths < MIN_TERM_MONTHS ||
    leaseTermMonths > MAX_TERM_MONTHS
  ) {
    return undefined;
  }
  try {
    const start = parseISO(commencementDate);
    if (Number.isNaN(start.getTime())) return undefined;
    const end = addMonths(start, Number(leaseTermMonths));
    return format(end, "yyyy-MM-dd");
  } catch {
    return undefined;
  }
}

export function ContractInfoSection() {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();

  // Required-field highlights (Phase 6a.12/F-4, Spec §11.14.1).
  // `currency` moved to PaymentInfoSection in F-5 (Spec §11.15.3 — money row).
  const contractIdHighlight = useRequiredFieldHighlight("contractId");
  const assetClassHighlight = useRequiredFieldHighlight("assetClass");
  const commencementHighlight = useRequiredFieldHighlight("commencementDate");
  const leaseTermHighlight = useRequiredFieldHighlight("leaseTermMonths");

  // Ephemeral end-date input. NOT a form field — not in the Zod schema,
  // not persisted, not submitted. Single source of truth remains
  // `(commencementDate, leaseTermMonths)`; this input is a UX affordance
  // for the back-derivation scenario (auditor knows the contract end
  // date and wants the system to compute the term).
  const [endDateInput, setEndDateInput] = React.useState<string>("");

  const commencementDate = useWatch({
    control: form.control,
    name: "commencementDate",
  });
  const leaseTermMonths = useWatch({
    control: form.control,
    name: "leaseTermMonths",
  });

  // Forward sync: commencementDate / leaseTermMonths → endDate (display).
  // `setEndDateInput` is local state; it does NOT mark the form dirty,
  // so the auto-draft hook never sees a phantom edit when the form
  // hydrates from localStorage.
  React.useEffect(() => {
    setEndDateInput(
      computeEndIso(commencementDate, leaseTermMonths) ?? "",
    );
  }, [commencementDate, leaseTermMonths]);

  // Back-derivation: user picks an end date → derive `leaseTermMonths`.
  // This IS a real user edit, so we set `shouldDirty: true` to flow
  // through the auto-draft + state machine.
  function handleEndDateChange(raw: string) {
    setEndDateInput(raw);
    if (!raw || !commencementDate) return;
    try {
      const start = parseISO(commencementDate);
      const end = parseISO(raw);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return;
      }
      if (end <= start) return; // invalid order → silent no-op
      const months = differenceInMonths(end, start);
      if (months >= MIN_TERM_MONTHS && months <= MAX_TERM_MONTHS) {
        form.setValue("leaseTermMonths", months, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } catch {
      /* invalid date — silent no-op */
    }
  }

  return (
    <FormSectionCard
      title={t("form.section.contractInfo")}
      description={t("form.section.contractInfoDescription")}
      icon={<FileText className="h-4 w-4" />}
    >
      {/* Identity: contractId + assetClass; description full-width */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <FormField
          control={form.control}
          name="contractId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.contractId")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t("form.contractIdPlaceholder")}
                  {...field}
                  className={cn(INPUT_CHROME, contractIdHighlight)}
                />
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="assetClass"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.assetClass.label")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value ?? ""}>
                <FormControl>
                  <SelectTrigger
                    className={cn(INPUT_CHROME, assetClassHighlight)}
                    data-testid="asset-class-trigger"
                  >
                    <SelectValue placeholder={t("form.assetClass.placeholder")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ASSET_CLASS_ORDER.map((ac) => (
                    <SelectItem key={ac} value={ac}>
                      {t(`form.assetClass.${ac}` as never) as string}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessageT />
            </FormItem>
          )}
        />

        <div className="sm:col-span-2">
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={LABEL_FLEX}>
                  {t("form.description.label")}
                </FormLabel>
                <FormControl>
                  <Input
                    maxLength={200}
                    placeholder={t("form.description.placeholder")}
                    {...field}
                    value={field.value ?? ""}
                    className={INPUT_CHROME}
                  />
                </FormControl>
                <FormMessageT />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Term: commencement + lease term + (ephemeral) end date */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-5">
        <FormField
          control={form.control}
          name="commencementDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.commencementDate")}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="date"
                  {...field}
                  className={cn(INPUT_CHROME, commencementHighlight)}
                />
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="leaseTermMonths"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.leaseTermMonths")}
                <span className="text-destructive">*</span>
                <InfoTooltip
                  content={t("form.leaseTermMonthsHelp")}
                  ariaLabel={t("form.leaseTermMonthsHelpAria")}
                  data-testid="tooltip-lease-term"
                />
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={1200}
                  step={1}
                  {...field}
                  value={field.value ?? ""}
                  className={cn(
                    INPUT_CHROME,
                    leaseTermHighlight,
                    "text-right pr-3 font-mono tabular-nums",
                  )}
                />
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />

        <div>
          <Label htmlFor="endDate" className={LABEL_FLEX}>
            {t("form.endDate")}
          </Label>
          <Input
            id="endDate"
            type="date"
            value={endDateInput}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="bg-muted/50 text-muted-foreground border-dashed border-input shadow-sm"
            data-testid="contract-info-end-date"
          />
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid="contract-info-end-date-helper"
          >
            {t("form.endDateHelper")}
          </p>
        </div>
      </div>
    </FormSectionCard>
  );
}
